import * as React from 'react';
import { put, select, takeEvery, all } from 'redux-saga/effects';
import { isEmpty, isNil } from 'lodash';

import pagesEditorSaga from './PageEditor/saga';
import pagesCalendarSaga from './PagesHistoryCalendar/saga';
import homePageSaga from './PagesHistoryHome/saga';
import { pagesHistoryPageDetailsSaga } from './PagesHistoryHome/PageDetails/saga';
import { toast } from 'react-toastify';

import {
  fetchPagesHistory,
  loadPagesHistory,
  loadPagesHistoryError,
  refreshPagesHistory,
  setPagesHistoryFromExternalSource,
  pageSynced,
} from './actions';

import { StorageNames } from 'src/constants/storageNames';

import { createApiRequestSaga } from '../../modules/api/saga';
import { ENDPOINTS } from '../../modules/api/endpoints';
import { getType } from 'typesafe-actions';
import { importPagesFromFileFileReadSuccess } from '../../modules/importExport/actions';
import { isPageAnalyzed, SubscriptionState, TRIAL_PAGES_NUMBER } from 'src/Models';
import { getSubscriptionsState } from '../UserProfile/selectors';
import { FirstPageAnalysisBadge } from '../../components/FirstPageAnalysisBadge/index';

const migrateLegacyPage = (page: any) => {
  const { content, ...rest } = page;

  return {
    ...rest,
    text: content,
  };
};

function* loadPagesWorker() {
  try {
    const pagesHistory = localStorage.getItem(StorageNames.pagesHistory);

    if (isEmpty(pagesHistory) || isNil(pagesHistory)) {
      return;
    }

    const parsedPagesHistory = JSON.parse(pagesHistory);

    for (const pageId in parsedPagesHistory) {
      if (parsedPagesHistory.hasOwnProperty(pageId)) {
        parsedPagesHistory[pageId] = migrateLegacyPage(parsedPagesHistory[pageId]);
      }
    }

    yield put(setPagesHistoryFromExternalSource(parsedPagesHistory));

    localStorage.removeItem(StorageNames.pagesHistory);
    localStorage.setItem(`Legacy:${StorageNames.pagesHistory}`, pagesHistory);
  } catch (e) {
    yield put(loadPagesHistoryError(e.message));
  }
}

const fetchPagesSaga = createApiRequestSaga({
  actionSuccess: fetchPagesHistory.success,
  actionError: fetchPagesHistory.failure,
});

function* refreshPagesHistorySaga() {
  yield put(
    fetchPagesHistory.request({
      url: ENDPOINTS.PAGES.INDEX,
      method: 'get',
    }),
  );
}

function* fetchPagesSuccessSaga(action: ReturnType<typeof fetchPagesHistory.success>) {
  const pages = action.payload.reduce((map, p) => {
    map[p.dateId] = p;
    return map;
  }, {});

  yield put(setPagesHistoryFromExternalSource(pages));
}

function* loadPagesFromFileSaga(action: ReturnType<typeof importPagesFromFileFileReadSuccess>) {
  yield put(setPagesHistoryFromExternalSource(action.payload));
}

function* onPageSynced(action: ReturnType<typeof pageSynced>): IterableIterator<any> {
  const pageData = action.payload;
  const subscriptionState: SubscriptionState = yield select(getSubscriptionsState);
  const isAnalyzedPage = isPageAnalyzed(pageData);
  // Show toast for the first 3 analyzed pages
  const shouldShowToast = isAnalyzedPage && subscriptionState.trialAnalysisLeft > TRIAL_PAGES_NUMBER - 3;

  if (shouldShowToast) {
    toast(<FirstPageAnalysisBadge />, {
      autoClose: 10000,
      delay: 3000,
    });
  }
}

function* pagesHistoryWatcher() {
  yield all([
    yield takeEvery([getType(loadPagesHistory)], loadPagesWorker),
    yield takeEvery([getType(fetchPagesHistory.request)], fetchPagesSaga),
    yield takeEvery([getType(refreshPagesHistory)], refreshPagesHistorySaga),
    yield takeEvery([getType(fetchPagesHistory.success)], fetchPagesSuccessSaga),
    yield takeEvery([getType(pageSynced)], onPageSynced),
    yield takeEvery([getType(importPagesFromFileFileReadSuccess)], loadPagesFromFileSaga),
  ]);
}

export default function* rootSaga() {
  yield all([
    pagesHistoryWatcher(),
    pagesEditorSaga(),
    pagesCalendarSaga(),
    homePageSaga(),
    pagesHistoryPageDetailsSaga(),
  ]);
}



// WEBPACK FOOTER //
// ./src/containers/PagesHistory/saga.tsx